/**
 * Talent Tree UI
 *
 * Handles the talent tree interface:
 * - Panel rendering that replaces skill content
 * - Node click handling
 * - Tooltip display
 * - Respec functionality
 * - Integration with skill UIs
 */

import { gameState } from '../../game/gameState.js';
import { getTalentTreeData, NODE_STATES } from './talentTreeData.js';
import {
    getAvailablePoints,
    getSpentPoints,
    getEarnedPoints,
    getNextTalentPointLevel,
    getNodeState,
    getNodeRank,
    unlockNode,
    refundNodeRank,
    fullRespec,
    getFullRespecCost,
    getRespecCostForNode,
    getMasteryMultiplier,
    getTalentBonusSummary
} from './talentTree.js';
import { createTalentTreeRenderer } from './talentTreeRenderer.js';
import { toastSuccess, toastWarning, toastError } from '../../ui/toast.js';
import { formatWithCommas } from '../../utils/format.js';
import { showConfirmModal } from '../../ui/confirmModal.js';
import { skillRegistry } from '../../core/skillRegistry.js';

// Active renderer reference
let activeRenderer = null;
let activeSkillId = null;
let isRespecMode = false;
let hoveredNodeId = null;

// Preserve view state across re-renders
let savedViewState = null;

// Pending respec tracking - Map of nodeId -> ranksToRefund
let pendingRefunds = new Map();

/**
 * Render the talent tree panel for a skill
 * This replaces the normal skill content
 */
export function renderTalentTreePanel(skillId, containerSelector = '#skill-content') {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    const tree = getTalentTreeData(skillId);
    if (!tree) {
        container.innerHTML = `
            <div class="talent-tree-error">
                <p>No talent tree available for this skill yet.</p>
                <button class="btn" onclick="window.talentTreeUI.closeTalentTree()">Close</button>
            </div>
        `;
        return;
    }

    // Save current view state before re-rendering (for same skill)
    if (activeRenderer && activeSkillId === skillId) {
        savedViewState = {
            scale: activeRenderer.scale,
            offsetX: activeRenderer.offset.x,
            offsetY: activeRenderer.offset.y
        };
    } else {
        savedViewState = null; // New skill, start fresh
    }

    activeSkillId = skillId;
    isRespecMode = false;

    const availablePoints = getAvailablePoints(skillId);
    const spentPoints = getSpentPoints(skillId);
    const earnedPoints = getEarnedPoints(skillId);
    const nextPointLevel = getNextTalentPointLevel(skillId);
    const skill = gameState.getSkill(skillId);
    const masteryMult = getMasteryMultiplier(skillId);
    const respecCost = getFullRespecCost(skillId);
    const isFreeRespec = skill && skill.level >= 99;

    container.innerHTML = `
        <div class="talent-tree-panel">
            <div class="talent-tree-header">
                <div class="talent-tree-title">
                    <h2>${tree.name} Talents</h2>
                    ${masteryMult > 1 ? `<span class="mastery-badge">${masteryMult === 2 ? '2x' : '+50%'} Mastery</span>` : ''}
                </div>
                <div class="talent-tree-points">
                    <span class="points-available ${availablePoints > 0 ? 'has-points' : ''}">
                        ${availablePoints} points available
                    </span>
                    <span class="points-spent">(${spentPoints}/${earnedPoints} spent)</span>
                    ${nextPointLevel ? `<span class="points-next">Next at Lv ${nextPointLevel}</span>` : '<span class="points-next complete">All earned</span>'}
                </div>
                <div class="talent-tree-actions">
                    ${isRespecMode ? `
                        ${pendingRefunds.size > 0 ? `
                            <span class="pending-refund-info">
                                ${getTotalPendingRanks()} rank${getTotalPendingRanks() > 1 ? 's' : ''} selected
                                <span class="pending-cost">(${isFreeRespec ? 'Free' : formatWithCommas(getPendingRefundCost(skillId)) + 'g'})</span>
                            </span>
                            <button class="btn btn-success" onclick="window.talentTreeUI.confirmPendingRefunds()">
                                Confirm
                            </button>
                            <button class="btn btn-secondary" onclick="window.talentTreeUI.cancelPendingRefunds()">
                                Cancel
                            </button>
                        ` : `
                            <span class="respec-hint">Click nodes to mark for refund</span>
                        `}
                        <button class="btn btn-secondary respec-toggle active"
                                onclick="window.talentTreeUI.toggleRespecMode()">
                            Exit Respec
                        </button>
                    ` : `
                        <button class="btn btn-secondary respec-toggle"
                                onclick="window.talentTreeUI.toggleRespecMode()">
                            Respec Mode
                        </button>
                        ${spentPoints > 0 ? `
                            <button class="btn btn-danger full-respec"
                                    onclick="window.talentTreeUI.confirmFullRespec('${skillId}')">
                                Full Reset ${isFreeRespec ? '(Free)' : `(${formatWithCommas(respecCost)}g)`}
                            </button>
                        ` : ''}
                    `}
                    <button class="btn btn-close" onclick="window.talentTreeUI.closeTalentTree()">
                        ✕
                    </button>
                </div>
            </div>

            <div class="talent-tree-main">
                <div class="talent-tree-canvas-container">
                    <canvas id="talent-tree-canvas"></canvas>
                    <div id="talent-tooltip" class="talent-tooltip hidden"></div>
                </div>

                <div class="talent-tree-sidebar">
                    <div class="sidebar-section">
                        <h3 class="sidebar-title">Active Bonuses</h3>
                        <div class="sidebar-bonuses">
                            ${renderSidebarBonuses(skillId)}
                        </div>
                    </div>
                    <div class="sidebar-controls">
                        <button class="btn btn-small" onclick="window.talentTreeUI.resetView()">Reset View</button>
                        <span class="control-hint">Scroll to zoom, drag to pan</span>
                    </div>
                </div>
            </div>
        </div>
    `;

    // Initialize canvas
    initializeCanvas(skillId);
}

/**
 * Initialize the canvas and renderer
 */
function initializeCanvas(skillId) {
    const canvas = document.getElementById('talent-tree-canvas');
    if (!canvas) return;

    // Create renderer
    activeRenderer = createTalentTreeRenderer(canvas, skillId);

    // Restore saved view state if available (preserves zoom/pan across re-renders)
    if (savedViewState) {
        activeRenderer.scale = savedViewState.scale;
        activeRenderer.offset.x = savedViewState.offsetX;
        activeRenderer.offset.y = savedViewState.offsetY;
        savedViewState = null; // Clear after restoring
    }

    // Setup event listeners
    canvas.addEventListener('click', handleCanvasClick);
    canvas.addEventListener('mousemove', handleCanvasMouseMove);
    canvas.addEventListener('mousedown', (e) => activeRenderer?.onMouseDown(e));
    canvas.addEventListener('mousemove', (e) => activeRenderer?.onMouseMove(e));
    canvas.addEventListener('mouseup', () => activeRenderer?.onMouseUp());
    canvas.addEventListener('mouseleave', () => {
        activeRenderer?.onMouseUp();
        hideTooltip();
    });
    canvas.addEventListener('wheel', (e) => activeRenderer?.onWheel(e), { passive: false });

    // Start animation
    activeRenderer.startAnimation();
}

/**
 * Handle canvas click
 */
function handleCanvasClick(e) {
    if (!activeRenderer || !activeSkillId) return;

    // Ignore click if user was dragging (prevents accidental talent allocation)
    if (activeRenderer.wasDragging()) return;

    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const nodeId = activeRenderer.getNodeAtPosition(x, y);
    if (!nodeId) return;

    if (isRespecMode) {
        handleRespecClick(nodeId);
    } else {
        handleUnlockClick(nodeId);
    }
}

/**
 * Handle click to unlock a node
 */
function handleUnlockClick(nodeId) {
    const result = unlockNode(activeSkillId, nodeId);

    if (result.success) {
        // Re-render panel to update points display (no toast - visual feedback is enough)
        renderTalentTreePanel(activeSkillId);
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Handle click to mark/unmark a node for refund (in respec mode)
 * Each click adds 1 rank to refund. When all ranks are marked, next click clears it.
 */
function handleRespecClick(nodeId) {
    const tree = getTalentTreeData(activeSkillId);
    const node = tree.nodes[nodeId];
    if (!node) return;

    const currentRank = getNodeRank(activeSkillId, nodeId);
    if (currentRank <= 0) {
        toastWarning('Node has no ranks to refund');
        return;
    }

    const pendingRanks = pendingRefunds.get(nodeId) || 0;

    // If already at max pending, clear it (cycle back to 0)
    if (pendingRanks >= currentRank) {
        pendingRefunds.delete(nodeId);
        // Cascade: remove any prerequisites that are now invalid
        cascadeRemoveInvalidPending(activeSkillId);
    } else {
        // Check if we can add another rank to pending
        // Need to verify dependents won't break
        const newPendingRanks = pendingRanks + 1;
        const remainingRanks = currentRank - newPendingRanks;

        // If this would drop below max rank and dependents exist, check them
        if (remainingRanks < node.maxRank && !canRefundToRank(activeSkillId, nodeId, remainingRanks)) {
            toastWarning('Other nodes depend on this one being maxed');
            return;
        }

        pendingRefunds.set(nodeId, newPendingRanks);
    }

    // Update UI to show selection
    updateRespecHeader();
}

/**
 * Remove any pending refunds that are no longer valid
 * (e.g., a prerequisite was unmarked so dependents can't be refunded anymore)
 */
function cascadeRemoveInvalidPending(skillId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return;

    let changed = true;
    while (changed) {
        changed = false;
        for (const [nodeId, pendingRanks] of pendingRefunds.entries()) {
            const node = tree.nodes[nodeId];
            if (!node) continue;

            const currentRank = getNodeRank(skillId, nodeId);
            const remainingRanks = currentRank - pendingRanks;

            // If this node would drop below max, check if prerequisites still allow it
            if (remainingRanks < node.maxRank) {
                if (!canRefundToRank(skillId, nodeId, remainingRanks)) {
                    // This pending refund is no longer valid, remove it
                    pendingRefunds.delete(nodeId);
                    changed = true;
                    break; // Restart the loop since we modified the map
                }
            }
        }
    }
}

/**
 * Check if a node can be refunded to a specific rank
 * (considering dependents and their pending status)
 */
function canRefundToRank(skillId, nodeId, targetRank) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return false;

    const node = tree.nodes[nodeId];
    if (!node) return false;

    // If we'd still be at max rank, always ok
    if (targetRank >= node.maxRank) return true;

    // Check each node that has this as a prerequisite
    for (const [otherNodeId, otherNode] of Object.entries(tree.nodes)) {
        if (otherNode.prerequisites && otherNode.prerequisites.includes(nodeId)) {
            const otherRank = getNodeRank(skillId, otherNodeId);
            const otherPending = pendingRefunds.get(otherNodeId) || 0;
            const otherRemainingRanks = otherRank - otherPending;

            // If the dependent node will have ranks after refunds, we can't go below max
            if (otherRemainingRanks > 0) {
                return false;
            }
        }
    }

    return true;
}

/**
 * Get total pending ranks across all nodes
 */
function getTotalPendingRanks() {
    let total = 0;
    for (const ranks of pendingRefunds.values()) {
        total += ranks;
    }
    return total;
}

/**
 * Get total cost for pending refunds
 */
function getPendingRefundCost(skillId) {
    let totalCost = 0;
    for (const [nodeId, ranksToRefund] of pendingRefunds.entries()) {
        const costPerRank = getRespecCostForNode(skillId, nodeId);
        totalCost += costPerRank * ranksToRefund;
    }
    return totalCost;
}

/**
 * Update the respec header without full re-render
 */
function updateRespecHeader() {
    const actionsDiv = document.querySelector('.talent-tree-actions');
    if (!actionsDiv || !activeSkillId) return;

    const skill = gameState.getSkill(activeSkillId);
    const isFreeRespec = skill && skill.level >= 99;
    const spentPoints = getSpentPoints(activeSkillId);
    const respecCost = getFullRespecCost(activeSkillId);

    actionsDiv.innerHTML = `
        ${isRespecMode ? `
            ${pendingRefunds.size > 0 ? `
                <span class="pending-refund-info">
                    ${getTotalPendingRanks()} rank${getTotalPendingRanks() > 1 ? 's' : ''} selected
                    <span class="pending-cost">(${isFreeRespec ? 'Free' : formatWithCommas(getPendingRefundCost(activeSkillId)) + 'g'})</span>
                </span>
                <button class="btn btn-success" onclick="window.talentTreeUI.confirmPendingRefunds()">
                    Confirm
                </button>
                <button class="btn btn-secondary" onclick="window.talentTreeUI.cancelPendingRefunds()">
                    Cancel
                </button>
            ` : `
                <span class="respec-hint">Click nodes to mark for refund</span>
            `}
            <button class="btn btn-secondary respec-toggle active"
                    onclick="window.talentTreeUI.toggleRespecMode()">
                Exit Respec
            </button>
        ` : `
            <button class="btn btn-secondary respec-toggle"
                    onclick="window.talentTreeUI.toggleRespecMode()">
                Respec Mode
            </button>
            ${spentPoints > 0 ? `
                <button class="btn btn-danger full-respec"
                        onclick="window.talentTreeUI.confirmFullRespec('${activeSkillId}')">
                    Full Reset ${isFreeRespec ? '(Free)' : `(${formatWithCommas(respecCost)}g)`}
                </button>
            ` : ''}
        `}
        <button class="btn btn-close" onclick="window.talentTreeUI.closeTalentTree()">
            ✕
        </button>
    `;
}

/**
 * Confirm and process all pending refunds
 */
export function confirmPendingRefunds() {
    if (!activeSkillId || pendingRefunds.size === 0) return;

    const skill = gameState.getSkill(activeSkillId);
    const isFreeRespec = skill && skill.level >= 99;
    const totalCost = isFreeRespec ? 0 : getPendingRefundCost(activeSkillId);

    // Check gold
    if (totalCost > 0 && gameState.getGold() < totalCost) {
        toastError(`Need ${formatWithCommas(totalCost)} gold to refund`);
        return;
    }

    // Deduct gold
    if (totalCost > 0) {
        gameState.removeGold(totalCost);
    }

    // Refund pending ranks from each node
    let totalPoints = 0;
    for (const [nodeId, ranksToRefund] of pendingRefunds.entries()) {
        const currentRank = getNodeRank(activeSkillId, nodeId);
        const newRank = Math.max(0, currentRank - ranksToRefund);
        totalPoints += ranksToRefund;
        gameState.setTalentNodeRank(activeSkillId, nodeId, newRank);
    }

    toastSuccess(`Refunded ${totalPoints} rank${totalPoints > 1 ? 's' : ''}${totalCost > 0 ? ` (-${formatWithCommas(totalCost)}g)` : ''}`);

    // Clear pending and re-render
    pendingRefunds.clear();
    renderTalentTreePanel(activeSkillId);
    isRespecMode = true; // Stay in respec mode
}

/**
 * Cancel pending refunds
 */
export function cancelPendingRefunds() {
    pendingRefunds.clear();
    updateRespecHeader();
}

/**
 * Handle canvas mouse move for tooltips
 */
function handleCanvasMouseMove(e) {
    if (!activeRenderer || !activeSkillId) return;

    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const nodeId = activeRenderer.getNodeAtPosition(x, y);

    if (nodeId !== hoveredNodeId) {
        hoveredNodeId = nodeId;

        if (nodeId) {
            showTooltip(nodeId, e.clientX, e.clientY);
        } else {
            hideTooltip();
        }
    } else if (nodeId) {
        // Update tooltip position
        updateTooltipPosition(e.clientX, e.clientY);
    }
}

/**
 * Show tooltip for a node
 */
function showTooltip(nodeId, mouseX, mouseY) {
    const tooltip = document.getElementById('talent-tooltip');
    if (!tooltip) return;

    const tree = getTalentTreeData(activeSkillId);
    const node = tree.nodes[nodeId];
    if (!node) return;

    const state = getNodeState(activeSkillId, nodeId);
    const rank = getNodeRank(activeSkillId, nodeId);
    const masteryMult = getMasteryMultiplier(activeSkillId);

    // Build effect description
    let effectText = '';
    for (const effect of node.effects) {
        const baseValue = effect.perRank ? effect.value * node.maxRank : effect.value;
        const currentValue = effect.perRank ? effect.value * rank : (rank > 0 ? effect.value : 0);
        const masteryValue = currentValue * masteryMult;

        let effectName = effect.type;
        if (effect.proc) effectName = effect.proc;

        // Special handling for passiveGenerate (e.g., "1 ash per 5 crafts")
        if (effect.type === 'passiveGenerate') {
            const itemName = effect.item ? effect.item.replace(/_/g, ' ') : 'item';
            effectText += `<div class="effect-row">
                <span class="effect-name">Generates:</span>
                <span class="effect-value">1 ${itemName} per ${effect.value} crafts</span>
            </div>`;
            continue;
        }

        // Special handling for stackingSpeed (e.g., "+3% speed, 15% max")
        if (effect.type === 'stackingSpeed') {
            const perRankValue = (effect.value * 100).toFixed(0);
            const maxValue = effect.maxStack ? (effect.maxStack * 100).toFixed(0) : '?';
            effectText += `<div class="effect-row">
                <span class="effect-name">${effectName}:</span>
                <span class="effect-value">+${perRankValue}% per craft (${maxValue}% max)</span>
            </div>`;
            continue;
        }

        const formatValue = (v) => {
            if (effect.type === 'procChance' || effect.type === 'xpBonus' || effect.type === 'speedBonus' || effect.type === 'lootBonus' || effect.type === 'toolBonus' || effect.type === 'globalBonus') {
                return `${(v * 100).toFixed(0)}%`;
            }
            return v.toFixed(1);
        };

        if (rank > 0 && masteryMult > 1) {
            effectText += `<div class="effect-row">
                <span class="effect-name">${effectName}:</span>
                <span class="effect-value">${formatValue(masteryValue)}</span>
                <span class="effect-base">(base: ${formatValue(currentValue)})</span>
            </div>`;
        } else {
            effectText += `<div class="effect-row">
                <span class="effect-name">${effectName}:</span>
                <span class="effect-value">${formatValue(rank > 0 ? currentValue : baseValue)}</span>
                ${rank > 0 ? '' : '<span class="effect-base">(at max rank)</span>'}
            </div>`;
        }
    }

    // Build prerequisites text
    let prereqText = '';
    if (node.prerequisites && node.prerequisites.length > 0) {
        const prereqNames = node.prerequisites.map(id => tree.nodes[id]?.name || id).join(', ');
        // Handle "X of N" requirements (e.g., super capstones requiring 2 of 3)
        if (node.requirePrerequisites && node.requirePrerequisites < node.prerequisites.length) {
            prereqText = `<div class="prereq-row">Requires ${node.requirePrerequisites}: ${prereqNames} (maxed)</div>`;
        } else {
            prereqText = `<div class="prereq-row">Requires: ${prereqNames} (maxed)</div>`;
        }
    }

    // Respec cost if in respec mode
    let respecText = '';
    if (isRespecMode && rank > 0) {
        const skill = gameState.getSkill(activeSkillId);
        const isFree = skill && skill.level >= 99;
        const cost = isFree ? 0 : getRespecCostForNode(activeSkillId, nodeId);
        respecText = `<div class="respec-row">Refund cost: ${isFree ? 'Free' : `${formatWithCommas(cost)}g`}</div>`;
    }

    // State indicator
    let stateText = '';
    switch (state) {
        case NODE_STATES.LOCKED:
            stateText = '<span class="state-locked">Locked</span>';
            break;
        case NODE_STATES.AVAILABLE:
            stateText = '<span class="state-available">Click to unlock</span>';
            break;
        case NODE_STATES.PURCHASED:
            stateText = `<span class="state-purchased">${rank}/${node.maxRank}</span>`;
            break;
        case NODE_STATES.MAXED:
            stateText = '<span class="state-maxed">Maxed</span>';
            break;
    }

    tooltip.innerHTML = `
        <div class="tooltip-header">
            <span class="tooltip-name">${node.name}</span>
            ${stateText}
        </div>
        <div class="tooltip-desc">${node.description}</div>
        ${node.detailedDescription ? `<div class="tooltip-detailed">${node.detailedDescription}</div>` : ''}
        <div class="tooltip-effects">${effectText}</div>
        ${prereqText}
        ${respecText}
        <div class="tooltip-tier">Tier ${node.tier}</div>
    `;

    tooltip.classList.remove('hidden');
    updateTooltipPosition(mouseX, mouseY);
}

/**
 * Update tooltip position
 */
function updateTooltipPosition(mouseX, mouseY) {
    const tooltip = document.getElementById('talent-tooltip');
    if (!tooltip) return;

    // Position tooltip near mouse but ensure it stays on screen
    const rect = tooltip.getBoundingClientRect();
    const padding = 15;

    let left = mouseX + padding;
    let top = mouseY + padding;

    // Check right edge
    if (left + rect.width > window.innerWidth - padding) {
        left = mouseX - rect.width - padding;
    }

    // Check bottom edge
    if (top + rect.height > window.innerHeight - padding) {
        top = mouseY - rect.height - padding;
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
}

/**
 * Hide tooltip
 */
function hideTooltip() {
    const tooltip = document.getElementById('talent-tooltip');
    if (tooltip) {
        tooltip.classList.add('hidden');
    }
    hoveredNodeId = null;
}

/**
 * Toggle respec mode
 */
export function toggleRespecMode() {
    isRespecMode = !isRespecMode;

    // Clear pending refunds when exiting respec mode
    if (!isRespecMode) {
        pendingRefunds.clear();
    }

    updateRespecHeader();

    if (isRespecMode) {
        toastWarning('Click nodes to mark for refund, then confirm');
    }
}

/**
 * Update respec button state
 */
function updateRespecButton() {
    const btn = document.querySelector('.respec-toggle');
    if (btn) {
        btn.classList.toggle('active', isRespecMode);
        btn.textContent = isRespecMode ? 'Exit Respec' : 'Respec Mode';
    }
}

/**
 * Confirm and execute full respec
 */
export async function confirmFullRespec(skillId) {
    const cost = getFullRespecCost(skillId);
    const skill = gameState.getSkill(skillId);
    const isFree = skill && skill.level >= 99;

    const message = isFree
        ? 'This will reset all your talent points. At level 99, this is free!'
        : `This will reset all your talent points for <span class="gold-cost">${formatWithCommas(cost)}</span> gold.`;

    const confirmed = await showConfirmModal({
        title: 'Reset Talents',
        message: message,
        confirmText: isFree ? 'Reset' : `Reset (-${formatWithCommas(cost)}g)`,
        cancelText: 'Cancel',
        confirmClass: 'danger'
    });

    if (!confirmed) return;

    const result = fullRespec(skillId);

    if (result.success) {
        toastSuccess(`Refunded ${result.refundedPoints} points${result.goldCost > 0 ? ` (-${formatWithCommas(result.goldCost)}g)` : ''}`);
        renderTalentTreePanel(skillId);
    } else {
        toastError(result.reason);
    }
}

/**
 * Close talent tree and return to normal skill view
 */
export function closeTalentTree() {
    // Cleanup renderer
    if (activeRenderer) {
        activeRenderer.destroy();
        activeRenderer = null;
    }

    // Trigger re-render of the skill
    if (activeSkillId) {
        const skillId = activeSkillId;
        activeSkillId = null;

        // Use skill registry to re-render the skill directly
        const skill = skillRegistry.get(skillId);
        if (skill && skill.render) {
            skill.render();
        }

        // Also dispatch event for any custom handling (backwards compatibility)
        window.dispatchEvent(new CustomEvent('talentTreeClosed', { detail: { skillId } }));
    }
}

/**
 * Reset canvas view
 */
export function resetView() {
    if (activeRenderer) {
        activeRenderer.resetView();
    }
}

/**
 * Render sidebar bonuses - vertical layout for side panel
 */
function renderSidebarBonuses(skillId) {
    const bonuses = getTalentBonusSummary(skillId);

    if (bonuses.length === 0) {
        return '<div class="no-bonuses">Allocate talent points to gain bonuses</div>';
    }

    // Separate stat bonuses from procs
    const statBonuses = bonuses.filter(b =>
        b.label.includes('XP') || b.label.includes('Speed') ||
        b.label.includes('Loot') || b.label.includes('Tool')
    );
    const procBonuses = bonuses.filter(b => b.label.includes('Chance'));

    let html = '';

    // Stat bonuses section
    if (statBonuses.length > 0) {
        html += '<div class="sidebar-bonus-group">';
        html += '<div class="sidebar-group-title">Stat Bonuses</div>';
        html += statBonuses.map(bonus => `
            <div class="sidebar-bonus-row">
                <span class="sidebar-bonus-label">${bonus.label.replace(' Bonus', '')}</span>
                <span class="sidebar-bonus-value bonus-stat">${bonus.value}</span>
            </div>
        `).join('');
        html += '</div>';
    }

    // Proc bonuses section
    if (procBonuses.length > 0) {
        html += '<div class="sidebar-bonus-group">';
        html += '<div class="sidebar-group-title">Proc Chances</div>';
        html += procBonuses.map(bonus => {
            // Format proc name nicely
            const procName = bonus.label
                .replace(' Chance', '')
                .replace(/([A-Z])/g, ' $1')
                .trim();
            return `
                <div class="sidebar-bonus-row">
                    <span class="sidebar-bonus-label">${procName}</span>
                    <span class="sidebar-bonus-value bonus-proc">${bonus.value}</span>
                </div>
            `;
        }).join('');
        html += '</div>';
    }

    return html;
}

/**
 * Render a compact talent button for skill headers
 */
export function renderTalentButton(skillId) {
    const tree = getTalentTreeData(skillId);
    if (!tree) return ''; // No tree for this skill yet

    const availablePoints = getAvailablePoints(skillId);
    const hasPoints = availablePoints > 0;

    return `
        <button class="talent-header-btn ${hasPoints ? 'has-points' : ''}"
                onclick="window.talentTreeUI.openTalentTree('${skillId}')"
                title="${hasPoints ? `${availablePoints} points available` : 'View talents'}">
            <span class="talent-icon">✦</span>
            ${hasPoints ? `<span class="talent-notification">!</span>` : ''}
        </button>
    `;
}

/**
 * Open talent tree for a skill
 */
export function openTalentTree(skillId) {
    renderTalentTreePanel(skillId);
}

/**
 * Check if talent tree is currently open
 */
export function isTalentTreeOpen() {
    return activeSkillId !== null;
}

/**
 * Get the currently open skill ID
 */
export function getActiveSkillId() {
    return activeSkillId;
}

/**
 * Check if a node is pending refund (returns number of ranks pending, or 0)
 */
export function isNodePendingRefund(nodeId) {
    return pendingRefunds.get(nodeId) || 0;
}

/**
 * Check if we're in respec mode
 */
export function isInRespecMode() {
    return isRespecMode;
}

// Expose to window for onclick handlers
window.talentTreeUI = {
    openTalentTree,
    closeTalentTree,
    toggleRespecMode,
    confirmFullRespec,
    confirmPendingRefunds,
    cancelPendingRefunds,
    resetView,
    renderTalentButton,
    isTalentTreeOpen,
    getActiveSkillId,
    isNodePendingRefund,
    isInRespecMode
};
